import { useQuery } from '@tanstack/react-query';
import { Player, Shortlist, Email } from '@/lib/types';
import { useMemo, useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/hooks/use-auth';

interface DataCoordinatorResult {
  // Data
  players: Player[];
  shortlists: Shortlist[];
  emails: Email[];
  
  // Loading states
  isInitialLoading: boolean;
  isAnyLoading: boolean;
  loadingProgress: number; // 0-100
  
  // Error states
  hasErrors: boolean;
  errors: string[];
  
  // Utility functions
  refetchAll: () => void;
  isDataReady: boolean;
}

/**
 * Coordinates data loading across all dashboard components
 * Provides unified loading states and progress tracking
 */
export function useDataCoordinator(): DataCoordinatorResult {
  const { user, isLoading: isAuthLoading } = useAuth();
  const [isAuthStable, setIsAuthStable] = useState(false);
  
  // Wait for authentication to be stable before making API calls
  useEffect(() => {
    if (!isAuthLoading && user) {
      // Small delay to ensure auth state is fully established
      const timer = setTimeout(() => {
        setIsAuthStable(true);
      }, 200);
      return () => clearTimeout(timer);
    } else {
      setIsAuthStable(false);
    }
  }, [isAuthLoading, user]);
  
  // Only run queries if user is authenticated, auth is not loading, and auth state is stable
  const shouldFetchData = !!user && !isAuthLoading && isAuthStable;
  
  // Also check for session in localStorage as a safety check
  const hasSession = !!localStorage.getItem("sessionId");
  const canFetch = shouldFetchData && hasSession;
  
  // Core data queries with better error handling and consistent URLs
  const playersQuery = useQuery<Player[]>({
    queryKey: ["/api/players/"],
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: canFetch,
    retry: (failureCount, error: any) => {
      // Don't retry on auth errors at all
      if (error?.message?.includes('401') || error?.message?.includes('403') || error?.message?.includes('Unauthorized')) {
        console.warn('Authentication error in players query, not retrying:', error?.message);
        return false;
      }
      // Only retry once for other errors
      return failureCount < 1;
    },
    retryDelay: 1000, // 1 second delay between retries
  });

  const shortlistsQuery = useQuery<Shortlist[]>({
    queryKey: ["/api/shortlists/"],
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: canFetch,
    retry: (failureCount, error: any) => {
      if (error?.message?.includes('401') || error?.message?.includes('403') || error?.message?.includes('Unauthorized')) {
        console.warn('Authentication error in shortlists query, not retrying:', error?.message);
        return false;
      }
      return failureCount < 1;
    },
    retryDelay: 1000,
  });

  const emailsQuery = useQuery<Email[]>({
    queryKey: ["/api/emails/"],
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: canFetch,
    retry: (failureCount, error: any) => {
      if (error?.message?.includes('401') || error?.message?.includes('403') || error?.message?.includes('Unauthorized')) {
        console.warn('Authentication error in emails query, not retrying:', error?.message);
        return false;
      }
      return failureCount < 1;
    },
    retryDelay: 1000,
  });

  // Calculate loading states
  const queries = [playersQuery, shortlistsQuery, emailsQuery];
  
  // If auth is still loading or not stable, consider it as initial loading
  const isInitialLoading = isAuthLoading || !isAuthStable || (canFetch && queries.some(query => query.isLoading && !query.data));
  const isAnyLoading = isAuthLoading || !isAuthStable || queries.some(query => query.isLoading);
  
  // Calculate loading progress
  const loadingProgress = useMemo(() => {
    if (isAuthLoading) return 10; // Auth loading gets 10%
    if (!isAuthStable) return 20; // Auth stabilizing gets 20%
    
    if (!canFetch) return 100; // If we shouldn't fetch data, we're "done"
    
    const totalQueries = queries.length;
    const completedQueries = queries.filter(query => !query.isLoading).length;
    const progress = Math.round(20 + (completedQueries / totalQueries) * 80); // Remaining 80%
    
    return progress;
  }, [
    isAuthLoading, 
    isAuthStable, 
    canFetch, 
    playersQuery.isLoading,
    shortlistsQuery.isLoading,
    emailsQuery.isLoading
  ]);

  // Error handling - filter out auth errors as they're handled by global handlers
  const errors = queries
    .filter(query => query.error && !query.error?.message?.includes('401') && !query.error?.message?.includes('403'))
    .map(query => query.error?.message || 'Unknown error');
  
  const hasErrors = errors.length > 0;
  
  // Don't handle auth errors here - let the global handler in queryClient do it

  // Data ready state - all critical data is loaded and user is authenticated
  const isDataReady = canFetch && !isInitialLoading && !hasErrors;

  // Refetch all data
  const refetchAll = useCallback(() => {
    if (canFetch) {
      queries.forEach(query => query.refetch());
    }
  }, [canFetch, playersQuery.refetch, shortlistsQuery.refetch, emailsQuery.refetch]);

  // Memoize the returned object to prevent unnecessary re-renders
  return useMemo(() => ({
    // Data
    players: playersQuery.data || [],
    shortlists: shortlistsQuery.data || [],
    emails: emailsQuery.data || [],
    
    // Loading states
    isInitialLoading,
    isAnyLoading,
    loadingProgress,
    
    // Error states
    hasErrors,
    errors,
    
    // Utility functions
    refetchAll,
    isDataReady,
  }), [
    playersQuery.data,
    shortlistsQuery.data,
    emailsQuery.data,
    isInitialLoading,
    isAnyLoading,
    loadingProgress,
    hasErrors,
    errors,
    refetchAll,
    isDataReady
  ]);
} 